import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Mail,
    Phone,
    MapPin,
    Clock,
    CheckCircle2,
    Loader2,
    Send,
    ExternalLink,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { BIZ } from "../lib/constants";
import { api } from "../lib/api";

const SUBJECTS = [
    "General",
    "Product Question",
    "Special Order / Allocated",
    "Wedding / Event",
    "Corporate Gifting",
    "Wholesale",
    "Press / Partnership",
    "Other",
];

const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: `Contact ${BIZ.name}`,
    url: `${BIZ.siteUrl}/contact`,
    description: `Contact Liquor Studio in Clinton, Missouri. Call ${BIZ.phone}, email ${BIZ.email}, or send a message about special orders, weddings, allocated bourbon, gifting, and more.`,
    mainEntity: {
        "@type": "LocalBusiness",
        "@id": `${BIZ.siteUrl}/#business`,
        name: BIZ.name,
        telephone: BIZ.phone,
        email: BIZ.email,
        address: {
            "@type": "PostalAddress",
            streetAddress: BIZ.address.line1,
            addressLocality: BIZ.address.city,
            addressRegion: BIZ.address.state,
            postalCode: BIZ.address.zip,
            addressCountry: BIZ.address.country,
        },
        contactPoint: [
            {
                "@type": "ContactPoint",
                contactType: "customer service",
                telephone: BIZ.phone,
                email: BIZ.email,
                areaServed: "US-MO",
                availableLanguage: ["English"],
            },
        ],
    },
};

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "General",
        message: "",
        website: "",
    });
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!form.name.trim() || form.name.trim().length < 2)
            e.name = "Please add your name.";
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            e.email = "Please enter a valid email.";
        if (!form.message.trim() || form.message.trim().length < 10)
            e.message = "Message must be at least 10 characters.";
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;
        setStatus("loading");
        try {
            const data = await api.submitContact(form);
            setStatus("success");
            setMessage(data.message || "Thanks — we'll get back within 1 business day.");
        } catch (err) {
            setStatus("error");
            setMessage(
                err?.data?.detail?.[0]?.msg ||
                    err?.message ||
                    "Could not send message. Please try again."
            );
        }
    };

    return (
        <>
            <SEO
                title="Contact Us"
                description={`Contact Liquor Studio, Clinton MO. Call ${BIZ.phone}, email ${BIZ.email}, or use our form for special orders, weddings, allocated bourbon, and gifting questions.`}
                path="/contact"
                jsonLd={jsonLd}
            />

            {/* Header */}
            <section
                style={{
                    paddingTop: 160,
                    paddingBottom: 64,
                    borderBottom: "1px solid var(--ls-border)",
                }}
                data-testid="contact-header"
            >
                <div className="ls-container">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">Contact</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(44px, 7vw, 84px)",
                                fontWeight: 500,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Get in touch.<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                We answer fast.
                            </em>
                        </h1>
                        <p
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 620,
                            }}
                        >
                            Special orders, weddings, corporate gifting, allocated bottle holds —
                            or just a question about what to pour tonight. We reply within one
                            business day, usually a few hours.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact + Form */}
            <section className="section-y" data-testid="contact-content">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Contact info */}
                        <aside className="lg:col-span-4 flex flex-col gap-6" data-testid="contact-info">
                            <article
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 28,
                                }}
                                data-testid="contact-card-phone"
                            >
                                <Phone size={22} style={{ color: "var(--ls-gold)" }} />
                                <h2 className="font-display text-white mt-4" style={{ fontSize: 22, fontWeight: 500 }}>
                                    Call us
                                </h2>
                                <a
                                    href={`tel:${BIZ.phoneTel}`}
                                    className="link-underline text-white mt-3 inline-block"
                                    style={{ fontSize: 18, fontWeight: 500 }}
                                    data-testid="contact-phone-link"
                                >
                                    {BIZ.phone}
                                </a>
                                <p className="mt-2 text-sm" style={{ color: "var(--ls-text-muted)" }}>
                                    Best for urgent holds, allocated picks, wedding planning.
                                </p>
                            </article>

                            <article
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 28,
                                }}
                                data-testid="contact-card-email"
                            >
                                <Mail size={22} style={{ color: "var(--ls-gold)" }} />
                                <h2 className="font-display text-white mt-4" style={{ fontSize: 22, fontWeight: 500 }}>
                                    Email
                                </h2>
                                <a
                                    href={`mailto:${BIZ.email}`}
                                    className="link-underline text-white mt-3 inline-block"
                                    style={{ fontSize: 18, fontWeight: 500 }}
                                    data-testid="contact-email-link"
                                >
                                    {BIZ.email}
                                </a>
                                <p className="mt-2 text-sm" style={{ color: "var(--ls-text-muted)" }}>
                                    Great for special orders, gifting lists, press.
                                </p>
                            </article>

                            <article
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 28,
                                }}
                                data-testid="contact-card-address"
                            >
                                <MapPin size={22} style={{ color: "var(--ls-gold)" }} />
                                <h2 className="font-display text-white mt-4" style={{ fontSize: 22, fontWeight: 500 }}>
                                    Visit
                                </h2>
                                <p className="text-white mt-3" style={{ fontSize: 15, lineHeight: 1.6 }}>
                                    {BIZ.address.line1}<br />
                                    {BIZ.address.city}, {BIZ.address.state} {BIZ.address.zip}
                                </p>
                                <div
                                    className="mt-4"
                                    style={{
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        border: "1px solid var(--ls-border-strong)",
                                    }}
                                    data-testid="contact-map-wrap"
                                >
                                    <iframe
                                        title="Liquor Studio location map"
                                        src="https://www.google.com/maps?q=309+S+2nd+St+Clinton+MO+64735&output=embed"
                                        width="100%"
                                        height="220"
                                        style={{ border: 0, display: "block" }}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        allowFullScreen
                                        data-testid="contact-map-iframe"
                                    />
                                </div>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BIZ.address.full)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link-underline text-white mt-3 inline-flex items-center gap-1.5 text-sm"
                                    data-testid="contact-directions-link"
                                >
                                    Get Directions <ExternalLink size={12} />
                                </a>
                            </article>

                            <article
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 28,
                                }}
                                data-testid="contact-card-hours"
                            >
                                <Clock size={22} style={{ color: "var(--ls-gold)" }} />
                                <h2 className="font-display text-white mt-4" style={{ fontSize: 22, fontWeight: 500 }}>
                                    Hours
                                </h2>
                                <ul className="mt-3 text-sm" style={{ color: "var(--ls-text-secondary)", lineHeight: 1.8 }}>
                                    <li>Mon – Thu: 10 AM – 10 PM</li>
                                    <li>Fri – Sat: 10 AM – 11 PM</li>
                                    <li>Sun: 10 AM – 10 PM</li>
                                </ul>
                            </article>
                        </aside>

                        {/* Form */}
                        <div className="lg:col-span-8">
                            {status === "success" ? (
                                <div
                                    style={{
                                        background: "rgba(245, 176, 39, 0.06)",
                                        border: "1px solid rgba(245, 176, 39, 0.4)",
                                        borderRadius: 8,
                                        padding: 48,
                                        display: "flex",
                                        gap: 20,
                                        alignItems: "flex-start",
                                    }}
                                    data-testid="contact-success"
                                >
                                    <CheckCircle2 size={36} style={{ color: "var(--ls-gold)", flexShrink: 0 }} />
                                    <div>
                                        <h2
                                            className="font-display text-white"
                                            style={{ fontSize: 32, fontWeight: 500 }}
                                        >
                                            Message sent.
                                        </h2>
                                        <p
                                            className="mt-3"
                                            style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.6 }}
                                        >
                                            {message}
                                        </p>
                                        <p
                                            className="mt-3 text-sm"
                                            style={{ color: "var(--ls-text-muted)" }}
                                        >
                                            Need something urgent? Call us at{" "}
                                            <a
                                                href={`tel:${BIZ.phoneTel}`}
                                                className="link-underline text-white"
                                            >
                                                {BIZ.phone}
                                            </a>.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <form
                                    onSubmit={submit}
                                    noValidate
                                    style={{
                                        background: "var(--ls-panel)",
                                        border: "1px solid var(--ls-border)",
                                        borderRadius: 8,
                                        padding: 36,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 20,
                                    }}
                                    aria-label="Contact form"
                                    data-testid="contact-form"
                                >
                                    {/* Honeypot */}
                                    <input
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.website}
                                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                                        style={{ position: "absolute", left: "-9999px" }}
                                        aria-hidden="true"
                                    />

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="c-name" className="block text-sm font-medium text-white mb-2">
                                                Name <span style={{ color: "var(--ls-red)" }}>*</span>
                                            </label>
                                            <input
                                                id="c-name"
                                                type="text"
                                                required
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                className="ls-input"
                                                data-testid="contact-name-input"
                                                placeholder="Your full name"
                                            />
                                            {errors.name && (
                                                <p className="mt-1 text-xs" style={{ color: "var(--ls-red)" }}>{errors.name}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="c-email" className="block text-sm font-medium text-white mb-2">
                                                Email <span style={{ color: "var(--ls-red)" }}>*</span>
                                            </label>
                                            <input
                                                id="c-email"
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                className="ls-input"
                                                data-testid="contact-email-input"
                                                placeholder="you@example.com"
                                            />
                                            {errors.email && (
                                                <p className="mt-1 text-xs" style={{ color: "var(--ls-red)" }}>{errors.email}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="c-phone" className="block text-sm font-medium text-white mb-2">
                                                Phone <span style={{ color: "var(--ls-text-muted)" }}>(optional)</span>
                                            </label>
                                            <input
                                                id="c-phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                                className="ls-input"
                                                data-testid="contact-phone-input"
                                                placeholder="(660) 555-0100"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="c-subject" className="block text-sm font-medium text-white mb-2">
                                                Subject
                                            </label>
                                            <select
                                                id="c-subject"
                                                value={form.subject}
                                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                                className="ls-input"
                                                data-testid="contact-subject-select"
                                            >
                                                {SUBJECTS.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="c-message" className="block text-sm font-medium text-white mb-2">
                                            Message <span style={{ color: "var(--ls-red)" }}>*</span>
                                        </label>
                                        <textarea
                                            id="c-message"
                                            required
                                            rows={7}
                                            minLength={10}
                                            maxLength={2000}
                                            value={form.message}
                                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                                            className="ls-input"
                                            style={{ resize: "vertical", minHeight: 160 }}
                                            placeholder="Tell us what you're looking for, when you need it, and any special requests…"
                                            data-testid="contact-message-input"
                                        />
                                        <p className="mt-1.5 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                            {form.message.length}/2000
                                        </p>
                                        {errors.message && (
                                            <p className="mt-1 text-xs" style={{ color: "var(--ls-red)" }}>{errors.message}</p>
                                        )}
                                    </div>

                                    {status === "error" && (
                                        <p
                                            role="alert"
                                            className="text-sm"
                                            style={{ color: "var(--ls-red)" }}
                                            data-testid="contact-error"
                                        >
                                            {message}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={status === "loading"}
                                        className="btn-primary self-start"
                                        style={{ padding: "14px 28px", fontSize: 14 }}
                                        data-testid="contact-submit-btn"
                                    >
                                        {status === "loading" ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" /> Sending…
                                            </>
                                        ) : (
                                            <>
                                                Send Message <Send size={14} />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                        By submitting, you agree to our{" "}
                                        <a href="/privacy" className="link-underline text-white">Privacy Policy</a>.
                                        We never share your information. 21+ only for product inquiries.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
